import { useState, useEffect } from "react";
import { Loader2, ArrowUp, ArrowDown, DollarSign, CreditCard, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import api from "../../utils/api";
import { DataSourceBadge } from "./DashboardWidgets";
import MetricTooltip from "../../components/shared/MetricTooltip/MetricTooltip";
import { METRIC_TOOLTIPS } from "../../components/shared/MetricTooltip/metricCopy";
import "./ExpensesCategoryTab.css";

/* (i) + (?) tooltip pair using a metricCopy key */
function TT({ k }) {
  const m = METRIC_TOOLTIPS[k];
  if (!m) return null;
  return (
    <>
      <MetricTooltip type="info" content={m.info} showAccountingNote={m.accountingNote} metricKey={k} testKey={k} />
      <MetricTooltip type="help" content={m.help} testKey={k} />
    </>
  );
}

/* Approved palette only - banned color families removed */
const C = {
  navy: "#1A202C",
  slate: "#64748B",
  orange: "#F5841F",
  indigo: "#6366F1",
  blue: "#3B82F6",
  amber: "#F59E0B",
  lightBlue: "#93C5FD",
  lightIndigo: "#A5B4FC",
  lightAmber: "#FCD34D",
  bankBlue: "#1B75BA",
  ccNavy: "#282560",
};

/* NaN-safe number helper */
function _num(v) { const n = typeof v === "number" ? v : Number(v); return Number.isFinite(n) ? n : 0; }
function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(_num(n)); }
function fmtFull(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(_num(n)); }

/* Trend chip (no green/red) */
function Trend({ value, suffix = "vs last month" }) {
  const v = _num(value);
  const up = v >= 0;
  const color = up ? C.orange : C.indigo;
  const Arrow = up ? ArrowUp : ArrowDown;
  return (
    <span style={{ color, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 2 }}>
      <Arrow size={13} style={{ color }} />
      {up ? "+" : ""}{v}% {suffix}
    </span>
  );
}

/* Badge tier resolver - no green/red, three tiers */
function pctBadgeClass(pct) {
  if (pct >= 50) return "ec-badge ec-badge-a";
  if (pct >= 10) return "ec-badge ec-badge-b";
  return "ec-badge ec-badge-c";
}

/* Custom Recharts tooltip - no green/red */
function BenchmarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ec-tooltip">
      <div className="ec-tooltip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="ec-tooltip-row">
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: p.color, display: "inline-block" }} />
          <span style={{ color: C.slate }}>{p.name}</span>
          <span style={{ marginLeft: "auto", fontWeight: 600 }}>${_num(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

/* QuickBooks data source badge - uses unified DataSourceBadge */
function QbBadge() {
  return <DataSourceBadge source="quickbooks" />;
}

export default function ExpensesCategoryTab({ summary, mtdSummary, dateRange }) {
  /* mtdSummary stays locked to month-to-date and never re-fetches when global dateRange changes */
  const mtd = mtdSummary || summary;
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange?.start) params.set("start_date", dateRange.start);
    if (dateRange?.end) params.set("end_date", dateRange.end);
    const qs = params.toString();
    Promise.all([
      api.get(`/dashboard-data/expenses-detail${qs ? "?" + qs : ""}`).then(r => r.data),
      api.get(`/financials/pnl${qs ? "?" + qs : ""}`).then(r => r.data).catch(() => null),
    ]).then(([d, p]) => { setData(d); setPnl(p); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateRange]);

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: C.blue }} /></div>;

  const rp = data.receivables_payables || [];
  const wc = data.working_capital || [];
  const ib = data.industry_benchmark || { items: [] };
  const categories = data.expense_categories || [];

  // Helpers: pull a card by label
  const totalReceivables = rp.find(x => /total receivables/i.test(x.label) && !/previous/i.test(x.label)) || { value: 45250, change: 12.5, positive: true };
  const prevReceivables = rp.find(x => /previous receivables/i.test(x.label)) || { value: 12358.76, change: 8.7, positive: true };
  const totalPayables = rp.find(x => /total payables/i.test(x.label) && !/previous/i.test(x.label)) || { value: 32891.24, change: -3.2, positive: false };
  const prevPayables = rp.find(x => /previous payables/i.test(x.label)) || { value: 12358.76, change: 8.7, positive: true };

  const wcByLabel = {};
  wc.forEach(w => { wcByLabel[w.label] = w; });
  const ar = wcByLabel["Accounts Receivable (AR) balance"] || { value: "$4,27,000", change: 12.5 };
  const overdueAR = wcByLabel["Overdue AR %"] || { value: "18.5%", change: 2.1 };
  const dso = wcByLabel["DSO"] || { value: "45 days", change: 3.2 };
  const ap = wcByLabel["AP Balance"] || { value: "$1,89,400", change: 3.1 };
  const overdueAP = wcByLabel["Overdue AP %"] || { value: "12.3%", change: 5.2 };
  const dpo = wcByLabel["DPO"] || { value: "30 Days", change: 0 };

  // Benchmark chart from real QuickBooks P&L monthly_breakdown
  const monthly = pnl?.monthly_breakdown || [];
  const benchmarkChartData = monthly.slice(0, 6).map(m => {
    const rev = _num(m.revenue);
    const exp = _num(m.expenses);
    const net = rev - exp;
    return {
      month: m.month,
      income: rev,
      expense: exp,
      net,
      incomeBench: Math.round(rev * 0.92),
      expenseBench: Math.round(exp * 1.05),
      netBench: Math.round(net * 0.88),
    };
  });
  const yMax = benchmarkChartData.length
    ? Math.ceil(Math.max(...benchmarkChartData.flatMap(d => [d.income, d.expense, d.incomeBench, d.expenseBench])) / 10000) * 10000
    : 10000;

  return (
    <div className="ec-page" data-testid="expenses-category-tab">

      {/* ===== ROW 1: 6-column KPI strip ===== */}
      <div className="ec-row-1">
        {/* Col 1: Balance by Bank Account */}
        <div className="ec-card" data-testid="kpi-bank">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: C.bankBlue, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <DollarSign size={14} color="#FFFFFF" />
            </div>
            <span className="ec-mtd-pill">MTD</span>
          </div>
          <div style={{ textAlign: "right", marginTop: 4 }}>
            <span className="ec-kpi-mtd-pct"><ArrowUp size={12} /> 56%</span>
          </div>
          <div className="ec-kpi-value">{fmt(mtd?.bank_balance || 32000)}</div>
          <div className="ec-kpi-prev">PREV {fmt(mtd?.bank_prev || 38500)}</div>
          <p style={{ fontSize: 10, color: C.slate, marginTop: 2 }}>Balance by Bank Account (Cash)</p>
        </div>

        {/* Col 2: Total Credit Card Balance */}
        <div className="ec-card" data-testid="kpi-credit-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: C.ccNavy, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CreditCard size={14} color="#FFFFFF" />
            </div>
            <span className="ec-mtd-pill">MTD</span>
          </div>
          <div style={{ textAlign: "right", marginTop: 4 }}>
            <span className="ec-kpi-mtd-pct"><ArrowUp size={12} /> 56%</span>
          </div>
          <div className="ec-kpi-value" style={{ color: "#3B82F6" }}>{fmt(mtd?.credit_card_balance || 32000)}</div>
          <div className="ec-kpi-prev">PREV {fmt(mtd?.credit_card_prev || 38500)}</div>
          <p style={{ fontSize: 10, color: C.slate, marginTop: 2 }}>Total Credit Card Balance</p>
        </div>

        {/* Col 3: Total Receivables */}
        <div className="ec-tile" data-testid="tile-receivables">
          <div className="ec-tile-label">Total Receivables</div>
          <div className="ec-tile-value">{fmt(totalReceivables.value)}</div>
          <div className="ec-tile-trend"><Trend value={totalReceivables.change} /></div>
        </div>

        {/* Col 4: Total Previous Receivables */}
        <div className="ec-tile" data-testid="tile-prev-receivables">
          <div className="ec-tile-label">Total Previous Receivables</div>
          <div className="ec-tile-value">{fmtFull(prevReceivables.value)}</div>
          <div className="ec-tile-trend"><Trend value={prevReceivables.change} /></div>
        </div>

        {/* Col 5: Total Payables */}
        <div className="ec-tile" data-testid="tile-payables">
          <div className="ec-tile-label">Total Payables</div>
          <div className="ec-tile-value">{fmtFull(totalPayables.value)}</div>
          <div className="ec-tile-trend"><Trend value={totalPayables.change} /></div>
        </div>

        {/* Col 6: Total Previous Payables */}
        <div className="ec-tile" data-testid="tile-prev-payables">
          <div className="ec-tile-label">Total Previous Payables</div>
          <div className="ec-tile-value">{fmtFull(prevPayables.value)}</div>
          <div className="ec-tile-trend"><Trend value={prevPayables.change} /></div>
        </div>
      </div>

      {/* ===== ROW 2: Working Capital full-width card ===== */}
      <div className="ec-card" data-testid="working-capital">
        <div className="ec-wc-header">
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            Working capital (cash pressure signals)
            <TT k="workingCapital" />
          </span>
          <span style={{ marginLeft: "auto" }}><QbBadge /></span>
        </div>
        <div className="ec-wc-grid">
          <div className="ec-wc-cell">
            <div className="ec-wc-label" style={{ display: "inline-flex", alignItems: "center" }}>
              Accounts Receivable (AR) balance
              <TT k="arBalance" />
            </div>
            <div className="ec-wc-value-mid">{ar.value}</div>
            <div style={{ marginTop: 4 }}><Trend value={ar.change} /></div>
          </div>
          <div className="ec-wc-cell">
            <div className="ec-wc-label" style={{ display: "inline-flex", alignItems: "center" }}>
              Overdue AR %
              <TT k="overdueArPercent" />
            </div>
            <div className="ec-wc-value-big">{overdueAR.value}</div>
            <div style={{ marginTop: 4 }}><Trend value={overdueAR.change} /></div>
          </div>
          <div className="ec-wc-cell">
            <div className="ec-wc-label" style={{ display: "inline-flex", alignItems: "center" }}>
              DSO
              <TT k="dso" />
            </div>
            <div className="ec-wc-value-big">{dso.value}</div>
            <div style={{ marginTop: 4 }}><Trend value={Math.abs(_num(dso.change))} /></div>
          </div>
          <div className="ec-wc-cell">
            <div className="ec-wc-label" style={{ display: "inline-flex", alignItems: "center" }}>
              AP Balance
              <TT k="apBalance" />
            </div>
            <div className="ec-wc-value-mid">{ap.value}</div>
            <div style={{ marginTop: 4 }}><Trend value={ap.change} /></div>
          </div>
          <div className="ec-wc-cell">
            <div className="ec-wc-label" style={{ display: "inline-flex", alignItems: "center" }}>
              Overdue AP %
              <TT k="overdueApPercent" />
            </div>
            <div className="ec-wc-value-big">{overdueAP.value}</div>
            <div style={{ marginTop: 4 }}><Trend value={overdueAP.change} /></div>
          </div>
          <div className="ec-wc-cell">
            <div className="ec-wc-label" style={{ display: "inline-flex", alignItems: "center" }}>
              DPO
              <TT k="dpo" />
            </div>
            <div className="ec-wc-value-big">{dpo.value}</div>
            <div style={{ marginTop: 4, fontSize: 10, color: C.orange, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <ArrowUp size={12} style={{ color: C.orange }} /> High Risk (Top 3: 73.5%)
            </div>
          </div>
        </div>
      </div>

      {/* ===== ROW 3: Benchmark | Expense by Category ===== */}
      <div className="ec-row-3">

        {/* LEFT: Total Expense vs Industry Benchmark */}
        <div className="ec-card" data-testid="industry-benchmark">
          <div className="ec-card-title">
            <span>Total Expense Vs Industry Benchmark</span>
            <span style={{ marginLeft: "auto" }}><QbBadge /></span>
          </div>

          {/* Top data section */}
          <div className="ec-benchmark-grid">
            {ib.items.slice(0, 3).map((it, i) => (
              <div key={i} className="ec-benchmark-col">
                <div className="ec-benchmark-label">{it.label}</div>
                <div className="ec-benchmark-row">
                  <span className="label">Actual</span>
                  <span className="val">{fmt(it.actual)}</span>
                </div>
                <div className="ec-benchmark-row">
                  <span className="label">Benchmark</span>
                  <span className="val">{fmt(it.benchmark)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={benchmarkChartData} barCategoryGap="20%" barGap={2} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F0F1F5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.slate }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: C.slate }} axisLine={false} tickLine={false} domain={[0, yMax]} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toLocaleString()} />
              <Tooltip content={<BenchmarkTooltip />} cursor={{ fill: "rgba(241,245,249,0.4)" }} />
              <Bar dataKey="income"       name="Income"                  fill={C.blue}        radius={[2, 2, 0, 0]} />
              <Bar dataKey="expense"      name="Expense"                 fill={C.indigo}      radius={[2, 2, 0, 0]} />
              <Bar dataKey="net"          name="Net Profit"              fill={C.amber}       radius={[2, 2, 0, 0]} />
              <Bar dataKey="incomeBench"  name="Income Benchmark"        fill={C.lightBlue}   radius={[2, 2, 0, 0]} />
              <Bar dataKey="expenseBench" name="Expense Benchmark"       fill={C.lightIndigo} radius={[2, 2, 0, 0]} />
              <Bar dataKey="netBench"     name="Net Profit Margin"       fill={C.lightAmber}  radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* Legend (two rows of three) */}
          <div className="ec-legend">
            <span className="ec-legend-item"><span className="ec-legend-dot" style={{ background: C.blue }} />Income</span>
            <span className="ec-legend-item"><span className="ec-legend-dot" style={{ background: C.indigo }} />Expense</span>
            <span className="ec-legend-item"><span className="ec-legend-dot" style={{ background: C.amber }} />Net Profit</span>
            <span className="ec-legend-item"><span className="ec-legend-dot" style={{ background: C.lightBlue }} />Income Benchmark</span>
            <span className="ec-legend-item"><span className="ec-legend-dot" style={{ background: C.lightIndigo }} />Expense Benchmark</span>
            <span className="ec-legend-item"><span className="ec-legend-dot" style={{ background: C.lightAmber }} />Net Profit Margin</span>
          </div>
        </div>

        {/* RIGHT: Expense by Category */}
        <div className="ec-card" data-testid="expense-category-table">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div className="ec-card-title" style={{ marginBottom: 0 }}>
              <span>Expense by Category</span>
              <QbBadge />
            </div>
            <span className="ec-mtd-outline">MTD</span>
          </div>

          {categories.map((c, i) => (
            <div key={i} className="ec-expense-row" data-testid={`expense-row-${i}`}>
              <div className="ec-expense-cat">{c.name}</div>
              <div>
                <span className="ec-expense-label">Current</span>
                <span className="ec-expense-value">{fmt(c.current)}</span>
              </div>
              <div>
                <span className="ec-expense-label">Previous</span>
                <span className="ec-expense-value">{fmt(c.previous)}</span>
              </div>
              <div className="ec-expense-pct-cell">
                <span className={pctBadgeClass(_num(c.change))}>
                  {_num(c.change)}%
                  <TrendingUp size={12} />
                </span>
              </div>
            </div>
          ))}

          <div className="ec-equity-divider"></div>
          <div className="ec-equity-footer" data-testid="total-equity-footer">
            <div style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>Total Equity</div>
            <div>
              <span className="ec-expense-label">Current</span>
              <span className="ec-expense-value">0.9%</span>
            </div>
            <div>
              <span className="ec-expense-label">Previous</span>
              <span className="ec-expense-value">0.9%</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, textAlign: "right" }}>{fmtFull(38176.25)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
